import { createLocalizationProvider } from '@atlaskit/locale';

// Matches a plain ISO date string without time information (YYYY-MM-DD)
const ISO_YMD_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

// Format based on confidence with i18n: DAY -> D MMM, MONTH -> MMMM, QUARTER -> MMM–MMM
// Include year when the given date's year is not the current year
export function formatDateForDisplay(
	isoDate: string,
	locale: string = 'en',
	confidence?: 'DAY' | 'MONTH' | 'QUARTER',
): string {
	// For YYYY-MM-DD, construct a UTC midnight date; always format in UTC
	let date: Date;
	const ymdMatch = ISO_YMD_REGEX.exec(isoDate);
	if (ymdMatch) {
		const year = Number(ymdMatch[1]);
		const monthZeroBased = Number(ymdMatch[2]) - 1;
		const day = Number(ymdMatch[3]);
		// Construct in UTC so local timezone won't shift the calendar day
		date = new Date(Date.UTC(year, monthZeroBased, day));
	} else {
		// Fall back to built-in parsing for other date/time inputs
		date = new Date(isoDate);
	}

	// If parsing failed, return the original input unchanged
	if (isNaN(date.getTime())) {
		return isoDate;
	}

	try {
		const dateYear = date.getUTCFullYear();
		const currentYear = new Date().getUTCFullYear();
		const includeYear = dateYear !== currentYear;

		if (confidence === 'MONTH') {
			// Month (long), optionally append year
			const l10n = createLocalizationProvider(locale, {
				month: 'long',
				timeZone: 'UTC',
			});
			const parts = l10n.formatToParts(date);
			if (parts.month) {
				return includeYear ? `${parts.month} ${dateYear}` : parts.month;
			}
			return (
				new Intl.DateTimeFormat(locale, {
					month: 'long',
					timeZone: 'UTC',
				}).format(date) + (includeYear ? ` ${dateYear}` : '')
			);
		}

		if (confidence === 'QUARTER') {
			// Determine quarter bounds and show localized short month range
			const month = date.getUTCMonth(); // 0-11
			const quarter = Math.floor(month / 3); // 0-3
			const startMonth = quarter * 3;
			const endMonth = startMonth + 2;
			const startDate = new Date(Date.UTC(dateYear, startMonth, 1));
			const endDate = new Date(Date.UTC(dateYear, endMonth, 1));
			const l10n = createLocalizationProvider(locale, {
				month: 'short',
				timeZone: 'UTC',
			});
			const startParts = l10n.formatToParts(startDate);
			const endParts = l10n.formatToParts(endDate);
			const startLabel =
				startParts.month ||
				new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(startDate);
			const endLabel =
				endParts.month ||
				new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(endDate);
			return includeYear ? `${startLabel}–${endLabel} ${dateYear}` : `${startLabel}–${endLabel}`;
		}

		// Default (DAY) -> show day short month, optionally append year
		const l10n = createLocalizationProvider(locale, {
			day: 'numeric',
			month: 'short',
			timeZone: 'UTC',
		});
		const parts = l10n.formatToParts(date);
		if (parts.day && parts.month) {
			return includeYear
				? `${parts.day} ${parts.month} ${dateYear}`
				: `${parts.day} ${parts.month}`;
		}
		return (
			new Intl.DateTimeFormat(locale, {
				day: 'numeric',
				month: 'short',
				timeZone: 'UTC',
			}).format(date) + (includeYear ? ` ${dateYear}` : '')
		);
	} catch {
		// Final fallback using toLocaleDateString in UTC
		return date.toLocaleDateString(undefined, {
			month: 'short',
			day: 'numeric',
			timeZone: 'UTC',
		});
	}
}
